import React, { useState, useEffect } from 'react';
import { X, ShoppingBag, Utensils, Hotel, DollarSign, Calendar, RefreshCw } from 'lucide-react';
import { getSupabaseClient } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';

interface CustomerHistoryModalProps {
  party: any; // The selected customer object
  onClose: () => void;
}

export function CustomerHistoryModal({ party, onClose }: CustomerHistoryModalProps) {
  const { currentTenantId } = useAuth();
  const [activeTab, setActiveTab] = useState<'retail' | 'restaurant' | 'hotel' | 'ledger'>('retail');
  
  const [loading, setLoading] = useState<boolean>(false);
  const [retailPurchases, setRetailPurchases] = useState<any[]>([]);
  const [restaurantOrders, setRestaurantOrders] = useState<any[]>([]);
  const [hotelBookings, setHotelBookings] = useState<any[]>([]);
  const [customerPayments, setCustomerPayments] = useState<any[]>([]);
  
  // Ledger summary
  const [balanceSummary, setBalanceSummary] = useState<{ balance: number; balance_label: string } | null>(null);

  const customerId = party.originalId || party.id;

  const fetchData = async () => {
    const supabase = getSupabaseClient();
    if (!supabase || !currentTenantId || !customerId) return;

    setLoading(true);
    try {
      // 1. Retail Purchases
      const { data: retailData, error: retailError } = await supabase
        .from('sale_orders')
        .select('*')
        .eq('customer_id', customerId)
        .order('created_at', { ascending: false });
      
      if (!retailError && retailData) {
        setRetailPurchases(retailData);
      }

      // 2. Restaurant Orders
      const { data: restData, error: restError } = await supabase
        .from('orders')
        .select('*')
        .eq('customer_id', customerId)
        .order('created_at', { ascending: false });

      if (!restError && restData) {
        setRestaurantOrders(restData);
      }

      // 3. Hotel Bookings
      const { data: hotelData, error: hotelError } = await supabase
        .from('room_bookings')
        .select('*, rooms(room_number)')
        .eq('retail_customer_id', customerId)
        .order('created_at', { ascending: false });

      if (!hotelError && hotelData) {
        setHotelBookings(hotelData);
      }

      // 4. Ledger / RPC Balance
      const { data: balanceData, error: balanceError } = await supabase
        .rpc('get_customers_with_balance', { p_tenant_id: currentTenantId });

      if (!balanceError && balanceData) {
        const found = balanceData.find((c: any) => c.id === customerId);
        if (found) {
          setBalanceSummary({
            balance: Number(found.balance || 0),
            balance_label: found.balance_label || 'Settled'
          });
        } else {
          setBalanceSummary({ balance: 0, balance_label: 'Settled' });
        }
      }

      // 5. Customer Payments list
      const { data: paymentData, error: paymentError } = await supabase
        .from('customer_payments')
        .select('*')
        .eq('customer_id', customerId)
        .order('payment_date', { ascending: false });

      if (!paymentError && paymentData) {
        setCustomerPayments(paymentData);
      }

    } catch (e) {
      console.error('Error fetching customer history:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [customerId, currentTenantId]);

  return (
    <div className="fixed inset-0 bg-black/60 z-[110] flex items-center justify-center p-4 backdrop-blur-xs">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-4xl w-full h-[85vh] flex flex-col overflow-hidden border border-slate-200 dark:border-slate-800 animate-in fade-in zoom-in-95 duration-200">
        
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between bg-slate-50 dark:bg-slate-950/50">
          <div>
            <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
              <span>{party.name || party.customer_name || 'Guest'}</span>
              <span className="text-xs font-normal text-slate-500">History & Details</span>
            </h3>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              Phone: {party.phone || 'N/A'} · ID: {party.idProofNumber || party.id_proof_number || 'N/A'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={fetchData}
              className="h-8 w-8 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
              title="Refresh Data"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="h-8 w-8 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Tab Row */}
        <div className="flex border-b border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-950/20 px-6 gap-2 overflow-x-auto">
          {[
            { id: 'retail', label: 'Retail Purchases', icon: ShoppingBag },
            { id: 'restaurant', label: 'Restaurant Orders', icon: Utensils },
            { id: 'hotel', label: 'Hotel Bookings', icon: Hotel },
            { id: 'ledger', label: 'Payments / Ledger', icon: DollarSign }
          ].map((tab) => {
            const IconComp = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id as any)}
                className={`py-3 px-4 text-xs font-bold flex items-center gap-2 border-b-2 transition-all whitespace-nowrap ${
                  isActive
                    ? 'border-indigo-600 text-indigo-600 dark:border-indigo-400 dark:text-indigo-400'
                    : 'border-transparent text-slate-600 dark:text-slate-400 hover:text-slate-950 dark:hover:text-slate-200'
                }`}
              >
                <IconComp className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Content Body */}
        <div className="flex-1 overflow-y-auto p-6 bg-white dark:bg-slate-900">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-64 gap-3">
              <RefreshCw className="h-8 w-8 text-indigo-600 dark:text-indigo-400 animate-spin" />
              <p className="text-sm text-slate-500 dark:text-slate-400">Fetching customer history records...</p>
            </div>
          ) : (
            <div className="space-y-4">
              
              {/* 1. Retail Purchases */}
              {activeTab === 'retail' && (
                <div className="overflow-x-auto">
                  {retailPurchases.length === 0 ? (
                    <div className="text-center py-16 border border-dashed border-slate-150 dark:border-slate-800 rounded-2xl">
                      <ShoppingBag className="h-10 w-10 text-slate-300 dark:text-slate-700 mx-auto mb-2" />
                      <p className="text-sm font-semibold text-slate-500 dark:text-slate-400">Koi record nahi mila</p>
                      <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">Is customer ke liye koi retail orders registered nahi hain.</p>
                    </div>
                  ) : (
                    <table className="w-full text-left border-collapse min-w-[600px]">
                      <thead>
                        <tr className="border-b border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-950/20">
                          <th className="py-2.5 px-3 text-xs font-bold text-slate-500 dark:text-slate-400">Date</th>
                          <th className="py-2.5 px-3 text-xs font-bold text-slate-500 dark:text-slate-400">Order/Invoice Ref</th>
                          <th className="py-2.5 px-3 text-xs font-bold text-slate-500 dark:text-slate-400">Amount</th>
                          <th className="py-2.5 px-3 text-xs font-bold text-slate-500 dark:text-slate-400">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 dark:divide-slate-800/50">
                        {retailPurchases.map((ord) => (
                          <tr key={ord.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-950/10 text-xs text-slate-700 dark:text-slate-300">
                            <td className="py-3 px-3">
                              {ord.order_date || ord.created_at?.slice(0, 10) || 'N/A'}
                            </td>
                            <td className="py-3 px-3 font-medium text-slate-900 dark:text-slate-100">
                              {ord.sale_order_number || ord.invoice_number || 'N/A'}
                            </td>
                            <td className="py-3 px-3 font-bold text-slate-900 dark:text-slate-100">
                              ₹{Number(ord.total_amount || ord.final_amount || 0).toLocaleString('en-IN')}
                            </td>
                            <td className="py-3 px-3">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                                ord.status === 'completed' || ord.status === 'delivered' || ord.status === 'converted'
                                  ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-400 border border-emerald-200/50'
                                  : 'bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-400 border border-amber-200/50'
                              }`}>
                                {ord.status || 'Draft'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}

              {/* 2. Restaurant Orders */}
              {activeTab === 'restaurant' && (
                <div className="overflow-x-auto">
                  {restaurantOrders.length === 0 ? (
                    <div className="text-center py-16 border border-dashed border-slate-150 dark:border-slate-800 rounded-2xl">
                      <Utensils className="h-10 w-10 text-slate-300 dark:text-slate-700 mx-auto mb-2" />
                      <p className="text-sm font-semibold text-slate-500 dark:text-slate-400">Koi record nahi mila</p>
                      <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">Is customer ke liye koi restaurant orders ya bills nahi hain.</p>
                    </div>
                  ) : (
                    <table className="w-full text-left border-collapse min-w-[600px]">
                      <thead>
                        <tr className="border-b border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-950/20">
                          <th className="py-2.5 px-3 text-xs font-bold text-slate-500 dark:text-slate-400">Date</th>
                          <th className="py-2.5 px-3 text-xs font-bold text-slate-500 dark:text-slate-400">Table Number</th>
                          <th className="py-2.5 px-3 text-xs font-bold text-slate-500 dark:text-slate-400">Order Type</th>
                          <th className="py-2.5 px-3 text-xs font-bold text-slate-500 dark:text-slate-400">Amount</th>
                          <th className="py-2.5 px-3 text-xs font-bold text-slate-500 dark:text-slate-400">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 dark:divide-slate-800/50">
                        {restaurantOrders.map((ord) => (
                          <tr key={ord.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-950/10 text-xs text-slate-700 dark:text-slate-300">
                            <td className="py-3 px-3">
                              {ord.created_at?.slice(0, 10) || 'N/A'}
                            </td>
                            <td className="py-3 px-3 font-medium text-slate-900 dark:text-slate-100">
                              {ord.table_number || (ord.table_id ? `Table ${ord.table_id}` : 'None (Delivery/Takeaway)')}
                            </td>
                            <td className="py-3 px-3 capitalize">
                              {ord.order_type || 'Dine-In'}
                            </td>
                            <td className="py-3 px-3 font-bold text-slate-900 dark:text-slate-100">
                              ₹{Number(ord.total || ord.final_amount || 0).toLocaleString('en-IN')}
                            </td>
                            <td className="py-3 px-3">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                                ord.status === 'Completed' || ord.status === 'Paid' || ord.status === 'delivered'
                                  ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-400'
                                  : 'bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-400'
                              }`}>
                                {ord.status || 'Pending'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}

              {/* 3. Hotel Bookings */}
              {activeTab === 'hotel' && (
                <div className="overflow-x-auto">
                  {hotelBookings.length === 0 ? (
                    <div className="text-center py-16 border border-dashed border-slate-150 dark:border-slate-800 rounded-2xl">
                      <Hotel className="h-10 w-10 text-slate-300 dark:text-slate-700 mx-auto mb-2" />
                      <p className="text-sm font-semibold text-slate-500 dark:text-slate-400">Koi record nahi mila</p>
                      <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">Is guest ke liye koi hotel room bookings nahi mili hain.</p>
                    </div>
                  ) : (
                    <table className="w-full text-left border-collapse min-w-[700px]">
                      <thead>
                        <tr className="border-b border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-950/20">
                          <th className="py-2.5 px-3 text-xs font-bold text-slate-500 dark:text-slate-400">Check-In</th>
                          <th className="py-2.5 px-3 text-xs font-bold text-slate-500 dark:text-slate-400">Check-Out</th>
                          <th className="py-2.5 px-3 text-xs font-bold text-slate-500 dark:text-slate-400">Room</th>
                          <th className="py-2.5 px-3 text-xs font-bold text-slate-500 dark:text-slate-400">Purpose of Stay</th>
                          <th className="py-2.5 px-3 text-xs font-bold text-slate-500 dark:text-slate-400">Final Bill</th>
                          <th className="py-2.5 px-3 text-xs font-bold text-slate-500 dark:text-slate-400">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 dark:divide-slate-800/50">
                        {hotelBookings.map((b) => (
                          <tr key={b.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-950/10 text-xs text-slate-700 dark:text-slate-300">
                            <td className="py-3 px-3">
                              {b.check_in_date || 'N/A'}
                            </td>
                            <td className="py-3 px-3">
                              {b.check_out_date || 'N/A'}
                            </td>
                            <td className="py-3 px-3 font-medium text-slate-900 dark:text-slate-100">
                              {b.rooms?.room_number ? `Room ${b.rooms.room_number}` : 'Unassigned'}
                            </td>
                            <td className="py-3 px-3 capitalize">
                              {b.purpose_of_stay || 'Leisure'}
                            </td>
                            <td className="py-3 px-3 font-bold text-slate-900 dark:text-slate-100">
                              ₹{Number(b.final_bill_amount || 0).toLocaleString('en-IN')}
                            </td>
                            <td className="py-3 px-3">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                                b.status === 'checked_out'
                                  ? 'bg-slate-100 text-slate-700 dark:bg-slate-850 dark:text-slate-400'
                                  : b.status === 'checked_in'
                                  ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-400'
                                  : 'bg-blue-100 text-blue-700 dark:bg-blue-950/50 dark:text-blue-400'
                              }`}>
                                {b.status?.replace('_', ' ') || 'booked'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}

              {/* 4. Payments/Ledger */}
              {activeTab === 'ledger' && (
                <div className="space-y-6">
                  
                  {/* Balance Summary Card */}
                  {balanceSummary && (
                    <div className="p-4 rounded-xl border border-slate-150 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-950/50 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                      <div>
                        <p className="text-xs text-slate-500 dark:text-slate-400 font-semibold uppercase tracking-wider">Account Balance Status</p>
                        <h4 className="text-lg font-extrabold text-slate-900 dark:text-slate-100 mt-0.5 flex items-center gap-2">
                          <span>₹{Math.abs(balanceSummary.balance).toLocaleString('en-IN')}</span>
                          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                            balanceSummary.balance_label === 'Due'
                              ? 'bg-red-100 text-red-700 dark:bg-red-950/50 dark:text-red-400'
                              : balanceSummary.balance_label === 'Advance'
                              ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-400'
                              : 'bg-slate-100 text-slate-600 dark:bg-slate-800'
                          }`}>
                            {balanceSummary.balance_label}
                          </span>
                        </h4>
                      </div>
                      <div className="text-xs text-slate-500 dark:text-slate-400 sm:text-right max-w-sm">
                        {balanceSummary.balance_label === 'Due' 
                          ? 'Yeh amount customer se receive karna baaki hai.'
                          : balanceSummary.balance_label === 'Advance'
                          ? 'Customer ka advance credit balance system mein active hai.'
                          : 'Settle status: Account fully cleared.'}
                      </div>
                    </div>
                  )}

                  <div className="overflow-x-auto">
                    <p className="text-xs font-bold text-slate-900 dark:text-slate-100 mb-3 uppercase tracking-wide">Payments History Ledger</p>
                    {customerPayments.length === 0 ? (
                      <div className="text-center py-16 border border-dashed border-slate-150 dark:border-slate-800 rounded-2xl">
                        <DollarSign className="h-10 w-10 text-slate-300 dark:text-slate-700 mx-auto mb-2" />
                        <p className="text-sm font-semibold text-slate-500 dark:text-slate-400">Koi record nahi mila</p>
                        <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">Is customer ke liye koi payments registered nahi hain.</p>
                      </div>
                    ) : (
                      <table className="w-full text-left border-collapse min-w-[600px]">
                        <thead>
                          <tr className="border-b border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-950/20">
                            <th className="py-2.5 px-3 text-xs font-bold text-slate-500 dark:text-slate-400">Date</th>
                            <th className="py-2.5 px-3 text-xs font-bold text-slate-500 dark:text-slate-400">Amount</th>
                            <th className="py-2.5 px-3 text-xs font-bold text-slate-500 dark:text-slate-400">Payment Method</th>
                            <th className="py-2.5 px-3 text-xs font-bold text-slate-500 dark:text-slate-400">Reference / Receipt</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800/50">
                          {customerPayments.map((p) => (
                            <tr key={p.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-950/10 text-xs text-slate-700 dark:text-slate-300">
                              <td className="py-3 px-3">
                                {p.payment_date || p.created_at?.slice(0, 10) || 'N/A'}
                              </td>
                              <td className="py-3 px-3 font-bold text-slate-900 dark:text-slate-100">
                                ₹{Number(p.amount || 0).toLocaleString('en-IN')}
                              </td>
                              <td className="py-3 px-3 capitalize">
                                {p.payment_method || 'Cash'}
                              </td>
                              <td className="py-3 px-3 font-medium text-slate-900 dark:text-slate-100">
                                {p.receipt_number || p.reference_note || 'On Account'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              )}

            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between bg-slate-50 dark:bg-slate-950/50">
          <div className="flex items-center gap-1.5 text-xs text-slate-500">
            <span>Read-Only Mode</span>
          </div>
          <Button onClick={onClose} variant="secondary" className="text-xs h-9">
            Close
          </Button>
        </div>

      </div>
    </div>
  );
}
